import { useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * A titled card on the account and admin pages, foldable by its header.
 *
 * The header is the toggle, laid out like the person rows in "Rights and
 * access": chevron on the left, then the title with its description beneath,
 * then the card's icon on the right. Folded, exactly that line remains, so a
 * long settings page can be scanned by its headings instead of scrolled past.
 *
 * The heading keeps its `id` and the section keeps `aria-labelledby`, because
 * the account page's anchors and its side navigation point at them.
 *
 * Which cards start open follows three rules:
 *   - a page with no anchor opens its first card, so it never reads as empty;
 *   - an anchor opens exactly the card it names and folds the rest — that is
 *     what following a link to one setting should do;
 *   - clicking a header opens or closes that card alone. Cards are not an
 *     exclusive set; the anchor is a starting point, not a mode.
 *
 * The anchor may name either the section id or the heading id, because links
 * to these pages are written both ways.
 */
function AccountCard({
  id,
  titleId,
  title,
  description,
  icon,
  /** The page's first card, opened when no anchor says otherwise. */
  isFirst = false,
  className = '',
  children,
  hideToggle = false
}) {
  const { hash } = useLocation();

  const anchor = hash.replace('#', '');
  const isTargeted =
    anchor !== '' && (anchor === id || anchor === titleId);
  const hasAnchor = anchor !== '';

  const [isOpen, setOpen] = useState(
    hasAnchor ? isTargeted : isFirst,
  );
  const bodyId = useId();
  const sectionRef = useRef(null);

  // Following /account#interface while already on the page changes the hash
  // without remounting, so the cards have to answer to it: the one named opens,
  // the others fold. A click afterwards moves one card only.
  useEffect(() => {
    if (!hasAnchor) {
      return;
    }

    setOpen(isTargeted);
  }, [hasAnchor, isTargeted, hash]);

  // A link inside the app changes the page without the browser's own jump
  // to the anchor ("Zurück zu den archivierten Artikeln"): the card named
  // scrolls into view itself, once it has opened.
  useEffect(() => {
    if (!isTargeted) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(() => {
      sectionRef.current?.scrollIntoView?.({ block: 'start' });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isTargeted, hash]);

  return (
    <section
      ref={sectionRef}
      id={id ?? titleId}
      className={['account-card', className].filter(Boolean).join(' ')}
      aria-labelledby={titleId}
    >
      <button
        type="button"
        className="account-card-header"
        aria-expanded={isOpen}
        aria-controls={bodyId}
        onClick={() => setOpen((open) => !open)}
      >
        {!hideToggle && (
          <i
            className={`fa-solid fa-chevron-right account-card-toggle${isOpen ? ' expanded' : ''
              }`}
            aria-hidden="true"
          />
        )}

        <div className="account-card-heading">
          <h2 id={titleId}>{title}</h2>

          {description && <p>{description}</p>}
        </div>

        {icon && <i className={icon} aria-hidden="true" />}
      </button>

      <div id={bodyId} className="account-card-body" hidden={!isOpen}>
        {children}
      </div>
    </section>
  );
}

export default AccountCard;
